import React, { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '../Button';
import {
    Nachkalkulation,
    NachkalkulationGruppe,
    NachkalkulationKunde,
} from '../../services/erpSteering';
import { formatCents } from '../../services/freelancerLogic';
import { brancheLabel } from '../../services/branchen';
import { NachkalkulationFilter } from '../../hooks/useErpCockpit';
import { KartenRahmen } from './KartenRahmen';

// Nachkalkulation: Erloese minus Freelancer-Kosten minus kalkulatorische
// Eigenleistung = Deckungsbeitrag, je Projekt und verdichtet nach Kunde,
// Branche und Region (ERP/CRM-Plan Abschnitt 8).
//
// Der Zeitraumfilter geht an die Datenbank (er entscheidet, welche Belege und
// Begehungen ueberhaupt gelesen werden); Kunde und Branche filtern hier im
// Client die bereits geladene Liste — ein zweiter Datenbankaufruf fuer eine
// Teilmenge derselben Zeilen waere Verschwendung.

interface Props {
    zustand: { daten: Nachkalkulation | null; laedt: boolean; fehler: string | null };
    kunden: NachkalkulationKunde[];
    filter: NachkalkulationFilter;
    onFilter: (f: NachkalkulationFilter) => void;
    onAnwenden: () => void;
    onOpenProject?: (projectId: string) => void;
}

const inputClass =
    'px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';
const labelClass = 'text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1';

const marge = (g: { margeProzent: number | null }): string =>
    g.margeProzent === null ? '—' : `${g.margeProzent.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;

const GruppenTabelle: React.FC<{ titel: string; gruppen: NachkalkulationGruppe[] }> = ({ titel, gruppen }) => (
    <div>
        <h3 className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">{titel}</h3>
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <tbody>
                    {gruppen.map((g) => (
                        <tr key={g.key} className="border-b border-slate-100">
                            <td className="py-1.5 pr-3 text-slate-700">{g.label}</td>
                            <td className="py-1.5 pr-3 text-right tabular-nums text-slate-500">{g.projekte}</td>
                            <td className="py-1.5 pr-3 text-right tabular-nums">{formatCents(g.erloeseCents)}</td>
                            <td
                                className={`py-1.5 pr-3 text-right font-semibold tabular-nums ${
                                    g.deckungsbeitragCents < 0 ? 'text-red-600' : 'text-emerald-700'
                                }`}
                            >
                                {formatCents(g.deckungsbeitragCents)}
                            </td>
                            <td className="py-1.5 text-right tabular-nums text-slate-500">{marge(g)}</td>
                        </tr>
                    ))}
                    {gruppen.length === 0 && (
                        <tr>
                            <td className="py-3 text-sm text-slate-400">Keine Daten.</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    </div>
);

export const NachkalkulationCard: React.FC<Props> = ({
    zustand,
    kunden,
    filter,
    onFilter,
    onAnwenden,
    onOpenProject,
}) => {
    const d = zustand.daten;

    const projekte = useMemo(() => {
        const alle = d?.projekte || [];
        return alle.filter(
            (p) =>
                (!filter.kundeId || p.kundeId === filter.kundeId) &&
                (!filter.branche || p.branche === filter.branche),
        );
    }, [d, filter.kundeId, filter.branche]);

    const branchen = useMemo(
        () => Array.from(new Set((d?.projekte || []).map((p) => p.branche).filter(Boolean))) as string[],
        [d],
    );

    return (
        <div className="space-y-5">
            {/* ── Filter ── */}
            <div className="flex flex-wrap items-end gap-3">
                <div>
                    <label className={labelClass} htmlFor="nk-von">Von</label>
                    <input
                        id="nk-von"
                        type="date"
                        className={`${inputClass} block`}
                        value={filter.von}
                        onChange={(e) => onFilter({ ...filter, von: e.target.value })}
                    />
                </div>
                <div>
                    <label className={labelClass} htmlFor="nk-bis">Bis</label>
                    <input
                        id="nk-bis"
                        type="date"
                        className={`${inputClass} block`}
                        value={filter.bis}
                        onChange={(e) => onFilter({ ...filter, bis: e.target.value })}
                    />
                </div>
                <Button variant="secondary" onClick={onAnwenden}>Zeitraum anwenden</Button>
                <div>
                    <label className={labelClass} htmlFor="nk-kunde">Kunde</label>
                    <select
                        id="nk-kunde"
                        className={`${inputClass} block`}
                        value={filter.kundeId}
                        onChange={(e) => onFilter({ ...filter, kundeId: e.target.value })}
                    >
                        <option value="">alle</option>
                        {kunden.map((k) => (
                            <option key={k.id} value={k.id}>{k.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={labelClass} htmlFor="nk-branche">Branche</label>
                    <select
                        id="nk-branche"
                        className={`${inputClass} block`}
                        value={filter.branche}
                        onChange={(e) => onFilter({ ...filter, branche: e.target.value })}
                    >
                        <option value="">alle</option>
                        {branchen.map((b) => (
                            <option key={b} value={b}>{brancheLabel(b)}</option>
                        ))}
                    </select>
                </div>
            </div>

            <KartenRahmen
                laedt={zustand.laedt}
                fehler={zustand.fehler}
                leer={!!d && projekte.length === 0}
                leerText="Kein Projekt mit Erlösen, Kosten oder Begehungen im Zeitraum."
            >
                {d && (
                    <div className="space-y-6">
                        {d.begehungenOhneSatz > 0 && (
                            <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                <p>
                                    <strong>{d.begehungenOhneSatz}</strong> Begehungen im Zeitraum haben keinen
                                    hinterlegten Eigenleistungssatz — sie gehen mit 0 EUR in den Deckungsbeitrag ein
                                    und lassen ihn zu gut aussehen. Sätze pflegen unter „Sätze“.
                                    Begehungen von Freelancern sind hier korrekt enthalten: ihre Kosten stehen in der
                                    Spalte „Fremdleistung“.
                                </p>
                            </div>
                        )}

                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-widest text-slate-500">
                                        <th className="py-2 pr-3 font-bold">Projekt</th>
                                        <th className="py-2 pr-3 font-bold">Kunde</th>
                                        <th className="py-2 pr-3 text-right font-bold">Erlöse</th>
                                        <th className="py-2 pr-3 text-right font-bold">Fremdleistung</th>
                                        <th className="py-2 pr-3 text-right font-bold">Eigenleistung</th>
                                        <th className="py-2 pr-3 text-right font-bold">Begehungen</th>
                                        <th className="py-2 pr-3 text-right font-bold">Deckungsbeitrag</th>
                                        <th className="py-2 text-right font-bold">Marge</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {projekte.map((p) => (
                                        <tr
                                            key={p.projectId}
                                            className={`border-b border-slate-100 ${
                                                p.deckungsbeitragCents < 0 ? 'bg-red-50/60' : 'hover:bg-slate-50/60'
                                            }`}
                                        >
                                            <td className="py-2 pr-3">
                                                <button
                                                    type="button"
                                                    className="text-left font-semibold text-slate-800 hover:text-blue-600 disabled:hover:text-slate-800"
                                                    disabled={!onOpenProject}
                                                    onClick={() => onOpenProject?.(p.projectId)}
                                                >
                                                    {p.projektNummer || '—'}
                                                </button>
                                                <p className="truncate text-xs text-slate-500">{p.projektName || ''}</p>
                                            </td>
                                            <td className="py-2 pr-3 text-slate-600">{p.kundeName || '—'}</td>
                                            <td className="py-2 pr-3 text-right tabular-nums">{formatCents(p.erloeseCents)}</td>
                                            <td className="py-2 pr-3 text-right tabular-nums">{formatCents(p.freelancerKostenCents)}</td>
                                            <td className="py-2 pr-3 text-right tabular-nums">{formatCents(p.eigenleistungCents)}</td>
                                            <td className="py-2 pr-3 text-right tabular-nums text-slate-500">
                                                {p.begehungen}
                                                {p.begehungenOhneSatz > 0 && (
                                                    <span className="ml-1 text-amber-700" title="ohne gepflegten Satz">
                                                        ({p.begehungenOhneSatz} o. S.)
                                                    </span>
                                                )}
                                            </td>
                                            <td
                                                className={`py-2 pr-3 text-right font-bold tabular-nums ${
                                                    p.deckungsbeitragCents < 0 ? 'text-red-600' : 'text-emerald-700'
                                                }`}
                                            >
                                                {formatCents(p.deckungsbeitragCents)}
                                            </td>
                                            <td className="py-2 text-right tabular-nums text-slate-500">{marge(p)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="border-t-2 border-slate-300 font-bold">
                                        <td className="py-2 pr-3" colSpan={2}>Gesamt (ungefiltert)</td>
                                        <td className="py-2 pr-3 text-right tabular-nums">{formatCents(d.gesamt.erloeseCents)}</td>
                                        <td className="py-2 pr-3 text-right tabular-nums">{formatCents(d.gesamt.freelancerKostenCents)}</td>
                                        <td className="py-2 pr-3 text-right tabular-nums">{formatCents(d.gesamt.eigenleistungCents)}</td>
                                        <td className="py-2 pr-3 text-right tabular-nums">{d.gesamt.begehungen}</td>
                                        <td
                                            className={`py-2 pr-3 text-right tabular-nums ${
                                                d.gesamt.deckungsbeitragCents < 0 ? 'text-red-600' : 'text-emerald-700'
                                            }`}
                                        >
                                            {formatCents(d.gesamt.deckungsbeitragCents)}
                                        </td>
                                        <td className="py-2 text-right tabular-nums">{marge(d.gesamt)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <div className="grid gap-6 lg:grid-cols-3">
                            <GruppenTabelle titel="Je Kunde" gruppen={d.jeKunde} />
                            <GruppenTabelle
                                titel="Je Branche"
                                gruppen={d.jeBranche.map((g) => ({
                                    ...g,
                                    label: g.key === 'unbekannt' ? g.label : brancheLabel(g.key),
                                }))}
                            />
                            <GruppenTabelle titel="Je Region" gruppen={d.jeRegion} />
                        </div>
                    </div>
                )}
            </KartenRahmen>
        </div>
    );
};
